import React, { useState } from 'react';
import { Menu, X, Star, IndianRupee, MessageCircle, Gamepad2, Youtube, Download, User, CreditCard, ShoppingCart, LogOut } from 'lucide-react';
import { Button } from './ui/button';

const iconMap = { Star, IndianRupee, MessageCircle, Gamepad2 };

const Header = ({ onLoginClick, branding, navLinks, banners, onSelectProduct, cartCount, onOrderNow, customer, onLogout, onAccountClick }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="w-full border-b border-neutral-200 bg-white sticky top-0 z-40 md:static">
      <div className="bg-neutral-900 text-neutral-100 text-[11px] px-3 py-1.5 flex items-center justify-between gap-2 overflow-hidden">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button data-testid="header-app-download-button" className="flex shrink-0 items-center gap-1 hover:text-white transition-colors">
            <Download className="w-3 h-3" />
            <span>App</span>
          </button>
          <span className="shrink-0 text-neutral-400">·</span>
          <span className="truncate">{branding?.tagline} | Enable Discount (₹4/pc)</span>
        </div>
        <a data-testid="header-godown-live-link" href="#live" className="hidden sm:flex shrink-0 items-center gap-1 text-red-400 hover:text-red-300 whitespace-nowrap">
          <Youtube className="w-3 h-3" /> GodownLIVE
        </a>
      </div>

      <div className="px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
        <a data-testid="header-brand-home-link" href="#top" className="flex min-w-0 items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-neutral-900 text-white grid place-items-center font-bold shrink-0">B</div>
          <div className="leading-tight min-w-0">
            <div className="font-extrabold text-neutral-900 text-[15px] sm:text-base truncate">
              {branding?.name}<span className="text-orange-500">.in</span>
            </div>
            <div className="text-[10px] text-neutral-500 truncate">{branding?.display_name}</div>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-1 text-[13px]">
          <span className="px-2 py-1 rounded bg-orange-50 text-orange-700 font-medium border border-orange-100">
            {branding?.sold_last_month} pcs sold in {branding?.month_year}
          </span>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Button data-testid="header-order-now-button" size="sm" onClick={onOrderNow} className="h-8 gap-1 sm:gap-1.5 px-2 sm:px-3 text-[13px] bg-orange-500 hover:bg-orange-600 text-white">
            <ShoppingCart className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Order Now</span>
            {cartCount > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-white text-orange-600 rounded-full">{cartCount}</span>
            )}
          </Button>
          {customer ? (
            <div className="flex items-center gap-1.5">
              <button data-testid="header-account-button" onClick={onAccountClick} className="inline-flex items-center gap-1 h-8 px-2 text-[13px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100">
                <User className="w-3.5 h-3.5" /> <span data-testid="header-customer-name" className="hidden sm:inline">{customer.name?.split(' ')[0] || 'Account'}</span>
              </button>
              <Button data-testid="header-logout-button" variant="outline" size="sm" onClick={onLogout} className="h-8 px-2 sm:px-3 text-[13px]">
                <span className="sm:hidden"><LogOut className="w-3.5 h-3.5" /></span><span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          ) : (
            <Button data-testid="header-login-button" variant="outline" size="sm" onClick={onLoginClick} className="h-8 gap-1 px-2 sm:px-3 text-[13px]">
              <User className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Login</span>
            </Button>
          )}
          <button data-testid="header-mobile-menu-button" className="md:hidden p-1.5 border border-neutral-200 rounded" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="hidden md:flex items-center gap-2 px-6 pb-2 text-[12px] overflow-x-auto">
        {(banners || []).map((b) => {
          const Icon = iconMap[b.icon] || Star;
          return (
            <a key={b.id} href="#promo" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 whitespace-nowrap transition-colors">
              <Icon className="w-3.5 h-3.5 text-orange-500" />
              <span className="text-neutral-700">{b.label}</span>
            </a>
          );
        })}
      </div>

      <nav className={`${menuOpen ? 'flex' : 'hidden'} md:flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-4 px-3 md:px-6 pb-3 md:pb-2 border-t md:border-t-0 border-neutral-100`}>
        {(navLinks || []).map((l) =>
          l.product_id ? (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => { onSelectProduct?.(l.product_id); setMenuOpen(false); }}
              className="text-[13px] text-neutral-700 hover:text-orange-600 transition-colors py-1 md:py-0 font-medium"
            >
              {l.label}
            </button>
          ) : (
            <a key={l.id} data-testid={`nav-link-${l.id}`} href={l.href} onClick={() => setMenuOpen(false)} className="text-[13px] text-neutral-700 hover:text-orange-600 transition-colors py-1 md:py-0">
              {l.label}
            </a>
          )
        )}
      </nav>
    </header>
  );
};

export default Header;
